import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import LoginStreetScene from "@/components/LoginStreetScene";
import { ArrowRight, RotateCw, AlertTriangle, Bus } from "lucide-react";

const ROLE_LABEL = { admin: "Yönetici", data_entry: "Veri Giriş", viewer: "Görüntüleyici" };

/**
 * OTP code input: 6 individual boxes, autofocus flow.
 */
function CodeBoxes({ value, onChange, onSubmit }) {
  const refs = useRef([]);
  const chars = value.padEnd(6, " ").split("").slice(0, 6);
  const handle = (idx, v) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    const next = value.split("");
    while (next.length < 6) next.push("");
    next[idx] = digit;
    const joined = next.join("").replace(/\s/g, "").slice(0, 6);
    onChange(joined);
    if (digit && idx < 5) refs.current[idx + 1]?.focus();
  };
  const keyDown = (idx, e) => {
    if (e.key === "Backspace" && !chars[idx].trim() && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter" && value.length === 6) onSubmit?.();
  };
  const paste = (e) => {
    const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (t) { e.preventDefault(); onChange(t); refs.current[Math.min(t.length, 5)]?.focus(); }
  };
  return (
    <div className="grid grid-cols-6 gap-2" data-testid="otp-code-boxes" onPaste={paste}>
      {chars.map((c, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          data-testid={`otp-digit-${i}`}
          value={c.trim()}
          onChange={(e) => handle(i, e.target.value)}
          onKeyDown={(e) => keyDown(i, e)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className="text-center outline-none"
          style={{
            width: "100%", height: 64,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 24, fontWeight: 600,
            background: "var(--y-surface)",
            border: "1px solid var(--y-border-strong)",
            borderRadius: 10,
            color: "var(--y-text)",
          }}
        />
      ))}
    </div>
  );
}

export default function Login() {
  const { finishOtpLogin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [step, setStep] = useState("pick");
  const [otpMeta, setOtpMeta] = useState(null);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);
  // Update 29.16 — Senkron kilit: Enter + butona basma gibi aynı tick'teki
  // çift verify isteklerini engeller (setVerifying state async olduğu için
  // yetmez). Başarılı verify sonrası true kalır ve geç gelen hata yanıtları
  // sessizce yutulur (örn. "kod kullanıldı").
  const verifyLockRef = useRef(false);
  const verifiedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/otp/eligible-users");
        setUsers(data || []);
      } catch {
        toast.error("Kullanıcı listesi yüklenemedi");
      } finally { setLoadingUsers(false); }
    })();
    return () => timerRef.current && clearInterval(timerRef.current);
  }, []);

  const selected = useMemo(() => users.find(u => u.id === selectedId), [users, selectedId]);

  const startCooldown = (sec) => {
    setCooldown(sec);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const requestOtp = async () => {
    if (!selectedId) return toast.error("Lütfen adınızı seçin");
    if (selected && !selected.has_phone) {
      return toast.error("Telefon numaranız tanımlı değil, lütfen yöneticinizle iletişime geçin");
    }
    setSending(true);
    try {
      const { data } = await api.post("/auth/otp/request", { user_id: selectedId });
      setOtpMeta(data);
      setStep("otp");
      setCode("");
      // Update 29.16 — Yeni kod isteği → verify kilidini sıfırla.
      verifyLockRef.current = false; verifiedRef.current = false;
      startCooldown(data.cooldown_sec || 60);
      toast.success(`Kod ${data.masked_phone} numarasına gönderildi`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kod gönderilemedi");
    } finally { setSending(false); }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error("6 haneli kodu girin");
    // Update 29.16 — Senkron kilit: aynı tick'te ikinci çağrıyı engelle.
    if (verifyLockRef.current) return;
    verifyLockRef.current = true;
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { user_id: selectedId, code });
      verifiedRef.current = true;
      // Update 29.16 — Login akışının asıl bug'ı: finishOtpLogin() await
      // edilmiyordu; refreshMe() tamamlanmadan navigate() tetiklendiği için
      // ProtectedRoute user=false görüp /login'e geri atıyordu.
      await finishOtpLogin(data);
      toast.success(`Hoş geldiniz, ${data.name}`);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      // Verify zaten başarılıysa (yarış koşullu ikinci yanıt) hatayı sessizce yut.
      if (verifiedRef.current) return;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kod doğrulanamadı");
      verifyLockRef.current = false;
    } finally {
      setVerifying(false);
    }
  };

  const goBack = () => {
    setStep("pick"); setCode(""); setOtpMeta(null);
    verifyLockRef.current = false; verifiedRef.current = false;
  };

  const fmtCooldown = (s) => {
    const m = Math.floor(s / 60);
    const r = String(s % 60).padStart(2, "0");
    return `${m}:${r}`;
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--y-bg)" }} data-testid="login-page">
      {/* ─── Left panel (desktop only) — animasyonlu sokak sahnesi ─── */}
      <div
        className="hidden lg:flex relative overflow-hidden"
        style={{
          flex: 1, minWidth: 0,
          background: "var(--y-sidebar)",
          borderRight: "1px solid var(--y-border)",
        }}
      >
        {/* Animasyonlu sahne — panelin ilk çocuğu, absolute inset:0 ile doldurur */}
        <LoginStreetScene />

        {/* Izgara dokusu — sahne üzerine hafif overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            zIndex: 1,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Sol üst logo (yarı saydam kutu) */}
        <div
          data-testid="login-logo-badge"
          style={{
            position: "absolute", top: 24, left: 24, zIndex: 2,
            display: "flex", alignItems: "center", gap: 12,
            background: "rgba(17,20,24,0.85)",
            borderRadius: 14,
            padding: "10px 16px 10px 10px",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
        >
          <div
            style={{
              width: 40, height: 40, borderRadius: 10,
              background: "var(--y-accent)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Bus className="w-5 h-5" style={{ color: "var(--y-accent-fg)" }} />
          </div>
          <div style={{ lineHeight: 1 }}>
            <div
              style={{
                fontFamily: "'Barlow Semi Condensed', sans-serif",
                fontWeight: 700, fontSize: 20, letterSpacing: "0.04em",
                color: "var(--y-text)",
              }}
            >
              YAZTUR
            </div>
            <div style={{ fontSize: 13, color: "#9BA3AE", marginTop: 6 }}>
              Filo operasyon
            </div>
          </div>
        </div>

        {/* Sol alt tag line */}
        <div
          data-testid="login-footer-tag"
          style={{
            position: "absolute", bottom: 24, left: 24, zIndex: 2,
            fontSize: 13, color: "#8A919C",
          }}
        >
          YAZTUR · Bursa Özel Halk Otobüsleri
        </div>
      </div>

      {/* ─── Right form ─── */}
      <div
        className="flex items-center justify-center p-4 sm:p-6 lg:p-10 w-full lg:w-[460px]"
        style={{ flexShrink: 0 }}
      >
        <div className="w-full">
          <div className="lg:hidden mb-8">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: "var(--y-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Bus className="w-4 h-4" style={{ color: "var(--y-accent-fg)" }} />
              </div>
              <div style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: 700, fontSize: 18, letterSpacing: "0.04em", color: "var(--y-text)" }}>
                YAZTUR
              </div>
            </div>
          </div>

          <h2
            style={{
              fontFamily: "'Barlow Semi Condensed', sans-serif",
              fontWeight: 600, fontSize: 36, letterSpacing: "-0.01em",
              color: "var(--y-text)",
            }}
            data-testid="login-title"
          >
            Giriş yap
          </h2>
          <p className="mt-3" style={{ fontSize: 14, color: "var(--y-muted)", lineHeight: 1.5 }}>
            {step === "pick"
              ? "Adınızı seçin, kayıtlı telefonunuza gelen kodu girin."
              : otpMeta && <>Kod <span className="mono" style={{ color: "var(--y-text)" }}>{otpMeta.masked_phone}</span> numarasına gönderildi ({otpMeta.ttl_minutes} dk geçerli).</>}
          </p>

          <div className="mt-8" data-testid="login-form">
            {step === "pick" ? (
              <div className="space-y-5">
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--y-label)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    Ad
                  </label>
                  <Select value={selectedId} onValueChange={setSelectedId} disabled={loadingUsers}>
                    <SelectTrigger
                      data-testid="login-user-select"
                      className="mt-2"
                      style={{
                        height: 52, background: "var(--y-surface)",
                        border: "1px solid var(--y-border-strong)", borderRadius: 12,
                        color: "var(--y-text)", fontSize: 15,
                      }}
                    >
                      <SelectValue placeholder={loadingUsers ? "Yükleniyor..." : "Adınızı seçin"} />
                    </SelectTrigger>
                    <SelectContent>
                      {users.length === 0 && !loadingUsers && (
                        <div className="px-3 py-4 text-sm" style={{ color: "var(--y-muted)" }}>
                          Kayıtlı kullanıcı yok.{" "}
                          <Link to="/admin-emergency" style={{ color: "var(--y-accent)" }} className="underline">
                            Acil yönetici girişi
                          </Link>
                        </div>
                      )}
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id} data-testid={`user-option-${u.id}`}>
                          <span className="flex items-center gap-3 text-sm">
                            <span style={{ fontWeight: 600, color: "var(--y-text)" }}>{u.name}</span>
                            <span className="mono" style={{ fontSize: 11, color: "var(--y-muted)" }}>
                              {ROLE_LABEL[u.role] || u.role}
                            </span>
                            {u.has_phone ? (
                              <span className="mono" style={{ fontSize: 11, color: "var(--y-success)" }}>{u.masked_phone}</span>
                            ) : (
                              <span className="mono" style={{ fontSize: 11, color: "var(--y-danger)" }}>Telefon yok</span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selected && !selected.has_phone && (
                  <div
                    className="flex items-start gap-2 rounded-[10px] p-3"
                    style={{ background: "rgba(255,92,92,0.08)", border: "1px solid rgba(255,92,92,0.35)" }}
                    data-testid="login-phone-missing-warning"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--y-danger)" }} />
                    <div style={{ fontSize: 13, color: "var(--y-text-2)" }}>
                      <div className="font-semibold" style={{ color: "var(--y-danger)" }}>Telefon numaranız tanımlı değil</div>
                      <div className="mt-1">Lütfen yöneticinizle iletişime geçin.</div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={requestOtp}
                  disabled={!selectedId || sending || (selected && !selected.has_phone)}
                  data-testid="send-otp-btn"
                  className="w-full"
                  style={{
                    height: 52, background: "var(--y-accent)", color: "var(--y-accent-fg)",
                    fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: 700, fontSize: 16,
                    borderRadius: 10,
                  }}
                >
                  {sending ? "Gönderiliyor..." : "SMS Kodu Gönder"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--y-label)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    6 Haneli Kod
                  </label>
                  <div className="mt-2">
                    <CodeBoxes value={code} onChange={setCode} onSubmit={verifyOtp} />
                  </div>
                  {/* Hidden input to keep test lookup working */}
                  <input type="hidden" data-testid="otp-code-input" value={code} readOnly />
                </div>

                <div className="flex items-center justify-between text-[13px]">
                  <button
                    onClick={goBack}
                    className="hover:underline"
                    style={{ color: "var(--y-muted)" }}
                    data-testid="otp-back-btn"
                  >
                    ← Adı değiştir
                  </button>
                  <button
                    onClick={requestOtp}
                    disabled={cooldown > 0 || sending}
                    data-testid="otp-resend-btn"
                    className="inline-flex items-center gap-1 disabled:opacity-50"
                    style={{ color: cooldown > 0 ? "var(--y-muted)" : "var(--y-accent)" }}
                  >
                    <RotateCw className={`w-3 h-3 ${sending ? "animate-spin" : ""}`} />
                    {cooldown > 0 ? `Tekrar gönder · ${fmtCooldown(cooldown)}` : "Kodu tekrar gönder"}
                  </button>
                </div>

                <Button
                  onClick={verifyOtp}
                  disabled={verifying || code.length !== 6}
                  data-testid="verify-otp-btn"
                  className="w-full"
                  style={{
                    height: 52, background: "var(--y-accent)", color: "var(--y-accent-fg)",
                    fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: 700, fontSize: 16,
                    borderRadius: 10,
                  }}
                >
                  {verifying ? "Doğrulanıyor..." : "Giriş Yap"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
